import React, { useState, useEffect } from 'react';
import { RobotState } from '../types';
import { Icons } from '../constants';

interface ActiveRobotProps {
  state: RobotState;
  onStop: () => void;
}

const TRADE_DURATION_SEC = 300; // 5 minutes
const SEARCH_DURATION_SEC = 5; // 5 seconds
const CYCLE_DURATION_SEC = TRADE_DURATION_SEC + SEARCH_DURATION_SEC;
const SESSION_DURATION_MS = 24 * 60 * 60 * 1000;

const ActiveRobot: React.FC<ActiveRobotProps> = ({ state, onStop }) => {
  const [timeLeft, setTimeLeft] = useState(TRADE_DURATION_SEC);
  const [sessionTimeStr, setSessionTimeStr] = useState('24:00:00');
  const [logs, setLogs] = useState<string[]>([]);

  // Initialize logs
  useEffect(() => {
    setLogs(["[SYSTEM] Neural Protocol v4.0.2 Initialized", "[SYNC] Offline Reconciliation Completed", "[SYNC] Broker Liquidity Feed Connected", "[AUTH] Neural Signature Verified"]);
  }, []);

  // Sync timers with engine state
  useEffect(() => {
    const timer = setInterval(() => {
      const now = Date.now();
      
      // Session Countdown
      if (state.startTime) {
        const elapsed = now - state.startTime;
        const remaining = Math.max(0, SESSION_DURATION_MS - elapsed);
        const h = Math.floor(remaining / (1000 * 60 * 60));
        const m = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
        const s = Math.floor((remaining % (1000 * 60)) / 1000);
        setSessionTimeStr(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`);

        // Sync local countdown with global cycle phase
        const cycleElapsedSec = Math.floor(elapsed / 1000) % CYCLE_DURATION_SEC;
        const isSearchingPhase = cycleElapsedSec < SEARCH_DURATION_SEC;

        if (isSearchingPhase) {
          setTimeLeft(TRADE_DURATION_SEC);
        } else {
          const tradeElapsedSec = cycleElapsedSec - SEARCH_DURATION_SEC;
          setTimeLeft(Math.max(0, TRADE_DURATION_SEC - tradeElapsedSec));
        }
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [state.startTime]);

  // Simulated logging activity
  useEffect(() => {
    if (!state.isSearching && state.currentTrade) {
      const activePair = state.currentTrade.pair;
      const logInterval = setInterval(() => {
        const potentialLogs = [
          `[ANALYSIS] ${activePair} Neural RSI divergence detected`,
          `[CORE] ${activePair} Neural weights optimized`,
          `[NETWORK] ${state.currentTrade?.broker} Latency 12ms - Optimal`,
          `[ALGO] Scalping ${activePair} micro-range volatility`,
          `[SYNC] Updating ${activePair} orderbook snapshots`,
          `[STRATEGY] Momentum factor for ${activePair} verified`,
          "[SECURITY] Capital guard protocol active"
        ];
        setLogs(prev => [potentialLogs[Math.floor(Math.random() * potentialLogs.length)], ...prev].slice(0, 10));
      }, 4000);
      return () => clearInterval(logInterval);
    }
  }, [state.isSearching, state.currentTrade]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const totalCurrentProfit = state.totalSessionProfit + state.unrealizedProfit;
  const isProfit = totalCurrentProfit >= 0;

  // Circular progress calculation
  const radius = 80;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (timeLeft / TRADE_DURATION_SEC) * circumference;

  return (
    <div className="max-w-3xl mx-auto space-y-4 animate-in fade-in zoom-in-95 duration-700">
      <div className="glass-card overflow-hidden shadow-2xl relative">
        <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden">
           <div className="animate-scan-line opacity-10"></div>
        </div>

        {/* Neural Profit Header */}
        <div className="p-4 md:p-6 text-center border-b border-white/5 bg-gradient-to-b from-[#AEB877]/[0.05] via-transparent to-transparent relative z-10">
          <div className="flex items-center justify-center gap-2 mb-3">
            <div className="px-2.5 py-1 bg-white/5 border border-white/10 rounded-full flex items-center gap-1.5">
              <span className="w-1 h-1 rounded-full bg-[#AEB877] shadow-[0_0_10px_#AEB877] animate-pulse"></span>
              <span className="text-[7px] font-black text-white/40 uppercase tracking-[0.3em]">Quantum_Processor_Active</span>
            </div>
          </div>
          
          <div className="relative inline-block mb-1">
            <div className={`absolute -inset-6 blur-[30px] opacity-20 transition-all duration-1000 ${isProfit ? 'bg-[#AEB877]' : 'bg-[#FF4D4D]'}`}></div>
            <p className={`text-3xl md:text-5xl font-black tabular-nums transition-all duration-700 tracking-tighter leading-none ${isProfit ? 'text-[#AEB877] glow-text' : 'text-[#FF4D4D]'}`}>
              {totalCurrentProfit < 0 ? '-' : ''}${Math.abs(totalCurrentProfit).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-6 mt-4">
            <div className="bg-white/5 border border-white/5 rounded-xl px-4 py-2 min-w-[120px]">
              <p className="text-[7px] font-black text-white/20 uppercase tracking-widest mb-0.5">Total_Realized</p>
              <p className="text-base font-black text-white tabular-nums tracking-tighter">
                {state.totalSessionProfit < 0 ? '-' : ''}${Math.abs(state.totalSessionProfit).toFixed(2)}
              </p>
            </div>
            <div className="w-px h-6 bg-white/5 hidden sm:block"></div>
            <div className="bg-white/5 border border-white/5 rounded-xl px-4 py-2 min-w-[120px]">
              <p className="text-[7px] font-black text-white/20 uppercase tracking-widest mb-0.5">Unrealized_PnL</p>
              <p className={`text-base font-black tabular-nums tracking-tighter ${state.unrealizedProfit >= 0 ? 'text-[#AEB877]' : 'text-[#FF4D4D]'}`}>
                {state.unrealizedProfit < 0 ? '-' : '+'}${Math.abs(state.unrealizedProfit).toFixed(2)}
              </p>
            </div>
          </div>
        </div>

        {/* State Visualizer */}
        <div className="p-4 md:p-6 relative z-10">
           {state.isSearching ? (
             <div className="flex flex-col items-center justify-center py-6 md:py-10 space-y-4">
                <div className="relative w-24 h-24 md:w-32 md:h-32">
                  <div className="absolute inset-0 rounded-full border border-white/5 bg-[radial-gradient(circle_at_50%_50%,_rgba(174,184,119,0.05)_0%,_transparent_70%)]"></div>
                  <div className="absolute inset-0 animate-radar origin-center">
                    <div className="w-1/2 h-full bg-gradient-to-l from-[#AEB877]/40 via-[#AEB877]/10 to-transparent rounded-l-full"></div>
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-10 h-10 bg-black border-2 border-[#AEB877] rounded-full flex items-center justify-center shadow-[0_0_30px_rgba(174,184,119,0.3)] animate-pulse">
                      <div className="w-5 h-5 border-4 border-[#AEB877] border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  </div>
                </div>
                <div className="text-center space-y-1.5">
                  <h3 className="text-lg md:text-xl font-black tracking-tighter uppercase text-white">Scanning_Entries</h3>
                  <p className="text-[7px] font-black text-[#AEB877] animate-flicker uppercase tracking-[0.6em]">Connecting to Tier-1 Liquidity Providers</p>
                </div>
             </div>
           ) : (
             <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
                <div className="relative flex items-center justify-center p-1.5">
                   <div className="absolute inset-0 bg-[#AEB877]/5 blur-[40px] rounded-full" />
                   <svg width="120" height="120" className="rotate-[-90deg] relative z-10">
                      <circle cx="60" cy="60" r="45" fill="transparent" stroke="rgba(255,255,255,0.05)" strokeWidth="10" />
                      <circle cx="60" cy="60" r="45" fill="transparent" stroke="#AEB877" strokeWidth="6" strokeDasharray={2 * Math.PI * 45} strokeDashoffset={(2 * Math.PI * 45) - (timeLeft / TRADE_DURATION_SEC) * (2 * Math.PI * 45)} strokeLinecap="round" className="transition-all duration-1000 ease-linear" />
                   </svg>
                   <div className="absolute inset-0 flex flex-col items-center justify-center text-center z-20">
                      <span className="text-2xl md:text-3xl font-black tabular-nums text-white tracking-tighter leading-none">{formatTime(timeLeft)}</span>
                   </div>
                </div>

                <div className="space-y-3">
                   <div className="bg-white/5 border border-white/5 rounded-[20px] p-4 shadow-2xl relative group overflow-hidden">
                      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#AEB877]/40 to-transparent"></div>
                      <div className="flex items-center justify-between">
                         <div className="flex items-center gap-2.5">
                            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform text-black">
                               <Icons.Chart />
                            </div>
                            <div>
                               <p className="text-lg font-black uppercase tracking-tighter text-white">{state.currentTrade?.pair || 'SECURE_ASSET'}</p>
                               <div className="flex items-center gap-1.5 mt-0.5">
                                  <span className="text-[7px] font-black text-white/20 uppercase tracking-widest">{state.currentTrade?.broker || 'TIER-1'}</span>
                                  <span className="text-[7px] font-black text-[#AEB877] uppercase tracking-widest">Live_Sync</span>
                               </div>
                            </div>
                         </div>
                         <div className={`px-2.5 py-1 rounded-lg text-[8px] font-black uppercase tracking-widest shadow-2xl ${state.currentTrade?.type === 'SELL' ? 'bg-[#FF4D4D] text-white' : 'bg-[#AEB877] text-black'}`}>
                            {state.currentTrade?.type || 'BUY'}
                         </div>
                      </div>
                   </div>

                   <div className="bg-black/40 border border-white/5 rounded-xl p-3 font-mono text-[7px] h-24 overflow-hidden relative shadow-inner">
                      <div className="space-y-1 opacity-60">
                        {logs.map((log, i) => (
                          <div key={i} className={`flex gap-1.5 truncate transition-all duration-700 ${i === 0 ? 'text-[#AEB877] opacity-100' : 'text-white/20'}`}>
                            <span className="shrink-0">[{new Date().toLocaleTimeString([], {hour12: false})}]</span>
                            <span className="truncate">{log}</span>
                          </div>
                        ))}
                      </div>
                   </div>
                </div>
             </div>
           )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="glass-card p-4 flex items-center justify-between group">
          <div>
            <p className="text-[7px] font-black text-white/20 uppercase tracking-widest mb-0.5">Protocol_End</p>
            <p className="text-lg font-black text-white tracking-tighter tabular-nums">{sessionTimeStr}</p>
          </div>
          <div className="w-8 h-8 rounded-xl bg-white/5 flex items-center justify-center group-hover:bg-[#AEB877]/10 transition-colors">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-white/20 group-hover:text-[#AEB877]"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
          </div>
        </div>
        
        <div className="md:col-span-2">
          <button 
            onClick={onStop} 
            className="w-full h-full bg-white/5 border border-red-500/20 text-[#FF4D4D] py-4 rounded-[20px] font-black text-base hover:bg-[#FF4D4D] hover:text-white transition-all shadow-2xl group flex items-center justify-center gap-2.5 uppercase tracking-tighter"
          >
            STOP ROBOT
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="group-hover:rotate-90 transition-transform"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActiveRobot;